import initSqlJs from "sql.js";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import { BASE_URL } from "../../global";
import { fetchAndStoreViews } from "./viewsLogic";
import { saveDbToIndexedDB } from "./indexedDbHelper";

const DB_STORAGE_KEY = "sqljs_db"; // key in localStorage
const userId = localStorage.getItem("id");

const tableList = [
  "instruments",
  "settings",
  "standards",
  "standardRanges",
  "standardInOut",
  "reservedSrfSeries",
  "cmc",
  "datasheetReadingsAdditionalInfo",
  "datasheetTemplate",
  "srfAnalytics",
  "KFactorReference",
  "uncertainty",
  "certificateStaticTables",
  "datasheetStaticTables",
  "notification",
  "remarks",
  "unit",
  "discipline",
  "clients",
  "clientSettings",
  "branch",
  "csrfForm",
  "addresses",
  "attendance",
  "userClientAssign",
  "users",

  "datasheets",
  "certificates",
  "srfInstruments",
  "datasheetStaticReadings",
  "srfs",
];

const excludedDataTables = [
  "srfInstruments",
  "datasheets",
  "certificates",
  "datasheetStaticReadings",
  "srfs",
];

const viewTableList = ["certificatesView"];

// --- Fetch all data from table ---
const fetchDataForTable = async (tableName) => {
  try {
    const sqlQuery = {
      query: `SELECT * FROM ${tableName} ${
        tableName === "users" ? `WHERE id = ${userId}` : ""
      }`,
    };
    const response = await axiosWithToken.post(`${BASE_URL}dynamic`, sqlQuery);
    return response.data;
  } catch (error) {
    console.error(`Error fetching data for table ${tableName}:`, error);
    return [];
  }
};

// --- Describe table schema ---
const fetchDescribeForTable = async (tableName) => {
  try {
    const sqlQuery = { query: `DESCRIBE ${tableName}` };
    const response = await axiosWithToken.post(`${BASE_URL}dynamic`, sqlQuery);
    return response.data;
  } catch (error) {
    console.error(`Error describing table ${tableName}:`, error);
    return [];
  }
};

// --- Create Table ---
const createTableFromSchema = (
  db,
  tableName,
  describeData,
  isExcludedTable
) => {
  let columnDefs = describeData
    .map(({ Field, Type, Null }) => {
      let type = "TEXT";
      if (Type.includes("int")) type = "INTEGER";
      else if (
        Type.includes("real") ||
        Type.includes("float") ||
        Type.includes("double")
      )
        type = "REAL";
      else if (Type.includes("blob")) type = "BLOB";

      const nullable = Null === "NO" ? "NOT NULL" : "";
      return `"${Field}" ${type} ${nullable}`.trim();
    })
    .join(", ");

  if (isExcludedTable) {
    const idColumn = describeData.find((col) => col.Field === "id");
    if (idColumn) {
      columnDefs = columnDefs.replace(
        /"id" \S+/,
        '"id" INTEGER PRIMARY KEY AUTOINCREMENT'
      );
    } else {
      columnDefs = `"id" INTEGER PRIMARY KEY AUTOINCREMENT, ${columnDefs}`;
    }
  }

  db.run(`CREATE TABLE IF NOT EXISTS "${tableName}" (${columnDefs});`);
};

// --- Main Function ---
export const fetchAndStoreAllTablesData = async () => {
  const SQL = await initSqlJs({
    locateFile: (file) => `sql-wasm.wasm`,
  });

  const db = new SQL.Database();
  const schemaMeta = [];

  const userId = localStorage.getItem("id");
  const userType = localStorage.getItem("type");

  let datasheets = []; // keep for later

  // ✅ Always create datasheetIds table
  db.run(`DROP TABLE IF EXISTS datasheetIds;`);
  db.run(`CREATE TABLE IF NOT EXISTS datasheetIds (id INTEGER PRIMARY KEY);`);

  for (const tableName of tableList) {
    try {
      const describeData = await fetchDescribeForTable(tableName);
      if (describeData.length === 0) continue;

      for (const col of describeData) {
        schemaMeta.push({
          tableName,
          field: col.Field,
          type: col.Type,
          nullable: col.Null,
          defaultValue: col.Default,
        });
      }

      const isExcludedTable = excludedDataTables.includes(tableName);
      createTableFromSchema(db, tableName, describeData, isExcludedTable);

      // Skip excluded tables unless type === "2"
      if (isExcludedTable && userType !== "2") continue;

      let data = [];

      if (!isExcludedTable) {
        // Normal case: fetch all data
        data = await fetchDataForTable(tableName);
      } else if (userType === "2") {
  // ✅ Helper: fetch data from dynamic endpoint
  const fetchData = async (query) => {
    const res = await axiosWithToken.post(`${BASE_URL}dynamic`, { query });
    return res.data || [];
  };

  // ✅ Fetch datasheets first
  if (tableName === "datasheets") {
    datasheets = await fetchData(`
      SELECT * FROM datasheets 
      WHERE assignedUser = ${userId} 
      AND jobAssignedDate >= DATE_SUB(NOW(), INTERVAL 3 DAY)
      AND (totalReadings < 1 OR totalReadings IS NULL)
    `);
    data = datasheets;

    // Refill datasheetIds table
    if (datasheets.length) {
      const insertIds = db.prepare(`INSERT INTO datasheetIds (id) VALUES (?);`);
      datasheets.forEach((d) => d.id && insertIds.run([d.id]));
      insertIds.free();
    }
  }

  // ✅ Extract datasheetIds once
  const datasheetIds = datasheets?.map((d) => d.id).join(",") || "";

  // ✅ Common logic for related tables
  const tableQueries = {
    certificates: `SELECT * FROM certificates WHERE id IN (${datasheetIds})`,
    srfInstruments: `SELECT * FROM srfInstruments WHERE id IN (${datasheetIds})`,
    datasheetStaticReadings: `SELECT * FROM datasheetStaticReadings WHERE datasheetId IN (${datasheetIds})`,
  };

  if (datasheetIds && tableQueries[tableName]) {
    data = await fetchData(tableQueries[tableName]);
  }

  
}

      // Insert into DB if data available
      if (data.length > 0) {
        const columns = describeData.map((col) => col.Field);
        const insertStmt = db.prepare(
          `INSERT INTO "${tableName}" (${columns.join(",")}) VALUES (${columns
            .map(() => "?")
            .join(",")})`
        );

        for (const row of data) {
          const values = columns.map((col) =>
            row[col] != null
              ? typeof row[col] === "number"
                ? row[col]
                : String(row[col])
              : ""
          );
          insertStmt.run(values);
        }

        insertStmt.free();
      }
    } catch (err) {
      console.error(`Error processing table ${tableName}:`, err);
    }
  }

  // ✅ Create and populate _table_schema
  db.run(`CREATE TABLE IF NOT EXISTS _table_schema (
    tableName TEXT,
    field TEXT,
    type TEXT,
    nullable TEXT,
    defaultValue TEXT
  );`);

  const insertSchemaStmt = db.prepare(
    `INSERT INTO _table_schema (tableName, field, type, nullable, defaultValue) VALUES (?, ?, ?, ?, ?)`
  );

  for (const row of schemaMeta) {
    insertSchemaStmt.run([
      row.tableName,
      row.field,
      row.type,
      row.nullable,
      row.defaultValue ?? "",
    ]);
  }
  insertSchemaStmt.free();

  // ✅ Import view logic
  await fetchAndStoreViews(db, viewTableList, axiosWithToken, BASE_URL);

 // ✅ Save DB to IndexedDB
const binaryArray = db.export();
await saveDbToIndexedDB(binaryArray);

  db.close();
};
